import React, { useState, useEffect } from 'react';

import ExtendedButton from '@src/stories/elements/ExtendedButton/ExtendedButton';
import ExtendedCartBreadcrumbs from "./ExtendedCartBreadcrumbs";
import { updateItems } from 'frontend/js/useShopifyCart';
import DatePicker from "react-datepicker";

import { formatPrice } from 'frontend/js/helpers';


const IconMarker = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="31" viewBox="0 0 7 31" fill="none">
    <path d="M1.35625 20.5205L0.30625 0H6.78125L5.6875 20.5205H1.35625ZM3.54375 31C2.52292 31 1.67708 30.6638 1.00625 29.9915C0.335417 29.3192 0 28.5153 0 27.5799C0 26.6445 0.335417 25.8553 1.00625 25.2122C1.67708 24.5398 2.52292 24.2037 3.54375 24.2037C4.56458 24.2037 5.39583 24.5398 6.0375 25.2122C6.67917 25.8553 7 26.6445 7 27.5799C7 28.5153 6.67917 29.3192 6.0375 29.9915C5.39583 30.6638 4.56458 31 3.54375 31Z" fill="black"/>
  </svg>
)

export default function ExtendedCartWrapper ({
  children,
  title,
  cart,
  settings,
  translations,
  activeIndex,
  disabled,
  buttonLabel,
  onNextStep,
  checkout,
  shipping_id,
  submitRef,
  error,
  isReviewModalOpen,
  errorMessage
}) {
  const [loading, setLoading ] = useState(disabled)
  const [onlyGiftCards, setOnlyGiftCards] = useState(false);
  const [hasGiftCard, setHasGiftCard] = useState(false);

  useEffect(()=> {
    setLoading(disabled);
  },[disabled])  

  useEffect(() => {
    // Filter out shipping cost items
    const nonShippingItems = cart.items.filter(item => item.id !== shipping_id);
    const hasGift = nonShippingItems.some(item => item.product_type === 'Gift Cards');
    const onlyGifts = nonShippingItems.length > 0 && nonShippingItems.every(item => item.product_type === 'Gift Cards');
    setHasGiftCard(hasGift);
    setOnlyGiftCards(onlyGifts);
  }, [cart.items, shipping_id]);

  const handleGiftCardsToCheckout = async () => {
    setLoading(true);           
    try {
      // Direct fetch call to update cart
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates: removeShippingItems }),
      });
      
      if (response.ok) {
        setTimeout(() => {
          submitRef.current.submit();
        }, 500);
      } else {
        console.error('Failed to update cart');
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  }

  

  const shippingItems = cart.items.filter((item) => item.id === shipping_id);
  const totalShippingPrice = shippingItems && shippingItems.reduce((acc, item) => acc + item.price, 0);
  const headerEl = document.querySelector('header');
  const headerHeight = headerEl?.getBoundingClientRect().height || 0;

  // Update and delete shipping cost items on initial cart journey load if minicart only has gift cards
  const removeShippingItems = shippingItems.reduce((acc, item) => {
    acc[item.key] = 0;
    return acc;
  }, {});

  const checkoutCartButtonLabel = onlyGiftCards ? 'Checkout' :  buttonLabel;
  return (
    <div className='extended-cart container lg:flex'>
      <div className='lg:w-2/3 py-4 lg:py-[54px] lg:px-6 flex flex-col gap-6'>
        <ExtendedCartBreadcrumbs
          translations={translations}
          activeIndex={isReviewModalOpen ? 2 : activeIndex}
          settings={settings}
        />

        <div className='flex justify-between items-center'>
          <h3 className='h3 extended-cart__title'>
            {title}
          </h3>

          <a
            href="/"
            className='underline text-lg'
          >
            {translations.back_to_shopping}
          </a>
        </div>

        {children}
      </div>

      <div  className={`lg:w-1/3 py-6 lg:p-6 flex flex-col gap-4 lg:z-10 lg:sticky lg:h-full  border-t lg:border-none mt-6 lg:mt-0`} style={{ top: `${headerHeight}px`, borderColor: '#AAA' }}>
        <div className='flex items-center justify-between'>
          <span className="h5 !font-500">{translations.total}</span>

          <span className="h5 !font-500">{formatPrice((cart.total_price - totalShippingPrice) || 0)}</span>
        </div>
        {
          ( checkout || onlyGiftCards ) &&  (
            <>
              <form id="checkout" action="/checkout" method="post" ref={submitRef}></form>
            </>
          )
        }
        { 
          <ExtendedButton
            label={ loading ? 'Updating cart...' : checkoutCartButtonLabel}
            style='primary'
            disabled={loading}
            icon={buttonLabel}
            onClick={async () => { 
              if (onlyGiftCards) {
                handleGiftCardsToCheckout();
              } else {
                onNextStep();
              }
            }}
          />
        }
      

        <div>
          <div className='h6 mb-2'>
            {settings.contact_us_title}
          </div>

          <p>{settings.contact_us_subtitle}</p>
        </div>

        <a
          className='flex items-center gap-4 no-underline'
          href={`mailto:${settings.contact_us_email}`}
        >
          <span className='icon-envelope text-2xl'></span>
          <span className='hover:underline'>
            {settings.contact_us_email}
          </span>
        </a>

        <a
          className='flex items-center gap-4 no-underline'
          href={`tel:${settings.contact_us_phone}`}
        >
          <span className='icon-phone text-2xl'></span>
          <span className='hover:underline'>
            {settings.contact_us_phone}
          </span>
        </a>

        { error && 
              ( 
                <div className="extended-cart__error-container"> 
                  <div className='extended-cart__error-container-icon'> 
                    <IconMarker/>
                  </div>
                  <div className='flex flex-col'> 
                    <div className='extended-cart__error-container-title'>  Make sure to check all information </div>
                    <div className='extended-cart__error-container-subtitle'> {errorMessage} </div>
                  </div>
                </div>
              )
        }

      { hasGiftCard && (
        <div className="extended-cart__disclaimer-container"> 
          <div className='flex flex-col'> 
            <div className='extended-cart__disclaimer-container-title'> {translations.gift_card_disclaimer} </div>
          </div>
        </div>
      )}
      
      </div>

 
    </div>
  )
}